import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { InlineStatus } from 'components/InlineStatus';
import { useOnboardingState } from 'state/useOnboardingState';
import { OnboardingLayout } from 'components/OnboardingLayout';
import { useOnboardingProgress } from 'hooks/useOnboardingProgress';
import { useCriticalAlertsOnboarding } from 'hooks/useCriticalAlertsOnboarding';
import { useForegroundState } from 'hooks/useForegroundState';
import { Loader } from 'components/Loader';
import { CountyNotifications } from '../CountyNotifications';
import { TurnOnNotificationsDownState } from '../TurnOnNotificationsDownState';
import { AddCountyDownState } from '../AddCountyDownState';

export const OnboardingNotificationsContent = (): JSX.Element => {
  const {
    isPushEnabled,
    pushToken,
    subscribedNotificationSettings,
    allNotificationSettings,
  } = useNotificationsSettings();
  const history = useHistory();
  const { setCompleted } = useOnboardingState();
  const { t } = useTranslation();
  const appState = useForegroundState();
  const { shouldShowCriticalAlertsStep } = useCriticalAlertsOnboarding();
  const progress = useOnboardingProgress('notifications');

  // Redirect to critical alerts step if push is enabled and critical alerts step should be shown
  useEffect(() => {
    if (appState.isActive && shouldShowCriticalAlertsStep(isPushEnabled)) {
      history.replace('/critical_alerts_permission');
    }
  }, [appState.isActive, shouldShowCriticalAlertsStep, isPushEnabled, history]);

  const handleDone = (): void => {
    setCompleted(true);
    history.push('/');
  };

  const title = t('onboardingNotifications.title');
  const buttonTitle = t('onboardingNotifications.button');
  const skipButtonTitle = t('notifications.skip');

  const { data, isLoading, isError } = allNotificationSettings;

  if (!isPushEnabled) {
    return (
      <OnboardingLayout
        buttonTitle={skipButtonTitle}
        buttonVariant="text"
        buttonColor="surface"
        onClick={handleDone}
        progress={progress}
        containerGap={1}
      >
        <TurnOnNotificationsDownState />
      </OnboardingLayout>
    );
  }

  if (!pushToken || isLoading) {
    return (
      <OnboardingLayout title={title} progress={progress}>
        <Loader disableBackground />
      </OnboardingLayout>
    );
  }

  if (isError || !data) {
    return (
      <OnboardingLayout
        title={title}
        buttonTitle={buttonTitle}
        onClick={handleDone}
        progress={progress}
      >
        <InlineStatus status="error" />
      </OnboardingLayout>
    );
  }

  const regionSettings = allNotificationSettings.data;
  const isAnyCountySelected = Boolean(
    subscribedNotificationSettings.data?.data.length,
  );

  if (!isAnyCountySelected) {
    return (
      <OnboardingLayout
        buttonTitle={skipButtonTitle}
        buttonVariant="text"
        buttonColor="surface"
        onClick={handleDone}
        progress={progress}
        containerGap={1}
      >
        <AddCountyDownState />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      title={title}
      buttonTitle={buttonTitle}
      onClick={handleDone}
      progress={progress}
    >
      <CountyNotifications regionSettings={regionSettings} />
    </OnboardingLayout>
  );
};
